import React, { type ComponentProps, type ReactNode, useMemo, useState } from 'react';

import { useIntl } from 'react-intl';

import { IconButton } from '@atlaskit/button/new';
import ArrowDownIcon from '@atlaskit/icon/core/arrow-down';
import ArrowUpIcon from '@atlaskit/icon/core/arrow-up';
import Pagination from '@atlaskit/pagination';
import { Inline, Stack } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import {
	type CharlieTableColumn,
	type CharlieTableRow,
	type TableSortDirection,
	useCharlieTable,
} from '@atlassian/charlie-table';

import { Table } from '../../common/ui/table';

import messages from './messages';

const DEFAULT_PAGE_SIZE = 10;

export type SortableColumn<T> = CharlieTableColumn<T> & {
	sortFunction?: (a: T, b: T, direction: TableSortDirection) => number;
};

interface Props<T> {
	columns: SortableColumn<T>[];
	data: CharlieTableRow<T>[];
	emptyState?: ReactNode;
	hasNext: boolean;
	isLoading?: boolean;
	loadingState?: ReactNode;
	onPageChange?(newPage: number): void;
	pageSize?: number;
}

const UpButton = ({ onClick }: { onClick: ComponentProps<typeof IconButton>['onClick'] }) => {
	const { formatMessage } = useIntl();

	return (
		<IconButton
			icon={(props) => <ArrowUpIcon {...props} color={token('color.icon.subtle')} size="small" />}
			onClick={onClick}
			label={formatMessage(messages.sortAscending)}
			appearance="subtle"
			spacing="compact"
		/>
	);
};

const DownButton = ({ onClick }: { onClick: ComponentProps<typeof IconButton>['onClick'] }) => {
	const { formatMessage } = useIntl();

	return (
		<IconButton
			icon={(props) => <ArrowDownIcon {...props} color={token('color.icon.subtle')} size="small" />}
			onClick={onClick}
			label={formatMessage(messages.sortDescending)}
			appearance="subtle"
			spacing="compact"
		/>
	);
};

const ColumnHeaderCell = <T extends object>({
	column,
	table,
}: {
	column: CharlieTableColumn<T>;
	table: ReturnType<typeof useCharlieTable<T>>;
}) => {
	return (
		<Inline space="space.050" alignBlock="center">
			{column.content}
			{column.isSortable && (
				<>
					{table.getIsCurrentSort(column) ? (
						table.getSorting()?.direction === 'asc' ? (
							<UpButton
								onClick={() => table.setSorting({ field: column.key, direction: 'desc' })}
							/>
						) : (
							<DownButton
								onClick={() => table.setSorting({ field: column.key, direction: 'asc' })}
							/>
						)
					) : (
						<UpButton onClick={() => table.setSorting({ field: column.key, direction: 'desc' })} />
					)}
				</>
			)}
		</Inline>
	);
};

export const PaginatedCharlieTable = <T extends object>({
	data,
	columns,
	emptyState,
	pageSize,
	isLoading,
	loadingState,
	onPageChange,
	hasNext,
}: Props<T>): React.JSX.Element => {
	const [page, setPage] = useState<number>(1);
	const table = useCharlieTable<T>({ columns, rows: data });
	const currentPageRows = useMemo(
		() =>
			table.rows.slice(
				(page - 1) * (pageSize ?? DEFAULT_PAGE_SIZE),
				page * (pageSize ?? DEFAULT_PAGE_SIZE),
			),
		[page, pageSize, table.rows],
	);
	const sortedRows = useMemo(() => {
		const currentSorting = table.getSorting();
		if (!currentSorting) {
			return currentPageRows;
		}

		return currentPageRows.sort((a, b) => {
			const sortFunction = columns.find((col) => col.key === currentSorting.field)?.sortFunction;
			if (sortFunction) {
				return sortFunction(a.data, b.data, currentSorting.direction);
			}

			if (currentSorting.direction === 'asc') {
				// @ts-expect-error: data has generic type
				return a.data[currentSorting.field] - b.data[currentSorting.field];
			}
			// @ts-expect-error: data has generic type
			return b.data[currentSorting.field] - a.data[currentSorting.field];
		});
	}, [columns, currentPageRows, table]);
	const pages = useMemo(() => {
		if (data.length === 0) {
			return [1];
		}
		// If there is no overflowing items into next page
		if (data.length % (pageSize ?? DEFAULT_PAGE_SIZE) === 0) {
			// Add an extra page at the end if there is a next page
			if (hasNext) {
				return Array.from(
					Array(Math.floor(data.length / (pageSize ?? DEFAULT_PAGE_SIZE)) + 1).keys(),
					(_, i) => i + 1,
				);
			} else {
				return Array.from(
					Array(Math.floor(data.length / (pageSize ?? DEFAULT_PAGE_SIZE))).keys(),
					(_, i) => i + 1,
				);
			}
		} else {
			return Array.from(
				Array(Math.floor(data.length / (pageSize ?? DEFAULT_PAGE_SIZE)) + 1).keys(),
				(_, i) => i + 1,
			);
		}
	}, [data.length, hasNext, pageSize]);

	return (
		<Stack alignInline="center">
			<Table
				head={{
					cells: table.getColumnsInOrder().map((col) => ({
						key: col.key,
						content: <ColumnHeaderCell column={col} table={table} />,
						width: col.width ? `${col.width}px` : undefined,
					})),
				}}
				rows={
					isLoading
						? []
						: sortedRows.map((row, index) => ({
								key: row.groupKey ?? index.toString(),
								cells: table.getColumnsInOrder().map((col) => ({
									content: col.cell.render(row.data),
								})),
							}))
				}
				emptyState={isLoading ? loadingState : emptyState}
			/>
			{pageSize && data.length > pageSize && (
				<Pagination
					pages={pages}
					onChange={(_, page) => {
						onPageChange?.(page);
						setPage(page);
					}}
					selectedIndex={page - 1}
				/>
			)}
		</Stack>
	);
};
